import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import axiosClient from "../lib/axiosClient";
import { getResizedImage } from "../lib/clientImageResize";

/** Parallel XHR cap per wave — balances throughput vs mobile radio / server load. */
export const UPLOAD_BURST_CONCURRENCY = 3;

/**
 * Upload many files in waves of `concurrency` parallel POSTs (Promise.all per wave).
 * @param {(file: File, reportId: string|number) => Promise<unknown>} uploadFileAsync
 */
export async function uploadFilesBurstConcurrent(
  uploadFileAsync,
  files,
  reportId,
  concurrency = UPLOAD_BURST_CONCURRENCY
) {
  const list = Array.isArray(files) ? files.filter(Boolean) : [];
  const n = Math.max(1, Math.min(Number(concurrency) || UPLOAD_BURST_CONCURRENCY, 6));
  for (let i = 0; i < list.length; i += n) {
    const chunk = list.slice(i, i + n);
    await Promise.all(chunk.map((file) => uploadFileAsync(file, reportId)));
  }
}

/**
 * Employee report file upload + optional background attachment polling.
 * Logic preserved from App.js — structural extraction only.
 */
export function useReportUpload({ backendUrl, token, setErrors, setShowToast }) {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStats, setUploadStats] = useState({
    loaded: 0,
    total: 0,
    startTime: null,
    speed: 0,
    timeRemaining: 0,
  });
  const [attachmentStatuses, setAttachmentStatuses] = useState({});
  const [currentReportId, setCurrentReportId] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);
  const [uploadingFiles, setUploadingFiles] = useState(new Set());
  const [hasActiveUploads, setHasActiveUploads] = useState(false);
  const [hasActiveProcessing, setHasActiveProcessing] = useState(false);
  const [reportCompleted, setReportCompleted] = useState(false);
  const [progressByAttachmentId, setProgressByAttachmentId] = useState({});
  const [completedReportIdShown, setCompletedReportIdShown] = useState(null);
  const [uploadCancelTokens, setUploadCancelTokens] = useState({});
  const [uploadSession, setUploadSession] = useState(null);

  const resetEmployeeUploadState = useCallback(() => {
    setCurrentReportId(null);
    setAttachmentStatuses({});
    setUploadingFiles(new Set());
    setHasActiveUploads(false);
    setHasActiveProcessing(false);
    setReportCompleted(false);
    setProgressByAttachmentId({});
    setUploadSession(null);
    setUploadCancelTokens({});
    setCompletedReportIdShown(null);
  }, []);

  const stopPolling = useCallback(() => {
    setPollingInterval((current) => {
      if (current) clearInterval(current);
      return null;
    });
  }, []);

  const resetForLogout = useCallback(() => {
    stopPolling();
    setCurrentReportId(null);
    setAttachmentStatuses({});
    setUploadingFiles(new Set());
    setHasActiveUploads(false);
    setHasActiveProcessing(false);
    setReportCompleted(false);
    setProgressByAttachmentId({});
    setCompletedReportIdShown(null);
    setUploadCancelTokens({});
    setUploadSession(null);
  }, [stopPolling]);

  // Upload single file to backend temp storage
  // IMPORTANT: This uses POST /api/attachments/upload (NOT /api/attachments/presign)
  // The presign endpoint was removed - all uploads go through backend
  // Backend saves to temp storage, then background worker handles compression + Hetzner upload
  const uploadFileAsync = async (file, reportId) => {
    const tempId = `temp-${Date.now()}-${Math.random()}`;
    let attachment_id = null;

    let payloadFile = file;
    if (file.type.startsWith("image/")) {
      try {
        payloadFile = await getResizedImage(file);
      } catch (e) {
        console.warn("[UPLOAD] Client resize failed, using original:", file.name, e);
        payloadFile = file;
      }
    }

    setUploadingFiles((prev) => new Set(prev).add(tempId));
    setHasActiveUploads(true);
    // Do not set hasActiveProcessing here — that means server-side compression/S3 (tracked via polling only).

    setProgressByAttachmentId((prev) => ({
      ...prev,
      [tempId]: { loaded: 0, total: payloadFile.size },
    }));

    const cancelTokenSource = axios.CancelToken.source();
    setUploadCancelTokens((prev) => ({
      ...prev,
      [tempId]: cancelTokenSource,
    }));

    try {
      const formData = new FormData();
      // Multer parses multipart parts in order — send all text fields before the file so destination sees report_id.
      formData.append("report_id", String(reportId));
      formData.append("client_original_name", file.name || "");
      formData.append("file", payloadFile, file.name || payloadFile.name);

      const uploadEndpoint = `${backendUrl}/api/attachments/upload?report_id=${encodeURIComponent(
        String(reportId)
      )}`;
      console.log(`[UPLOAD] Uploading ${file.name} to ${uploadEndpoint}`);

      const uploadResponse = await axiosClient.post(uploadEndpoint, formData, {
        // Omit Content-Type: axios sets multipart/form-data with the correct boundary.
        cancelToken: cancelTokenSource.token,
        onUploadProgress: (progressEvent) => {
          const { loaded, total } = progressEvent;

          setProgressByAttachmentId((prev) => ({
            ...prev,
            [tempId]: { loaded, total },
          }));

          setAttachmentStatuses((prev) => ({
            ...prev,
            [tempId]: {
              status: "pending",
              filename: file.name,
              uploadProgress: total > 0 ? Math.round((loaded * 100) / total) : 0,
            },
          }));
        },
      });

      attachment_id = uploadResponse.data.attachment_id;
      const { status } = uploadResponse.data;

      setProgressByAttachmentId((prev) => {
        const newProgress = { ...prev };
        if (newProgress[tempId]) {
          newProgress[attachment_id] = {
            ...newProgress[tempId],
            loaded: payloadFile.size,
            total: payloadFile.size,
          };
          delete newProgress[tempId];
        }
        return newProgress;
      });

      setAttachmentStatuses((prev) => {
        const newStatuses = { ...prev };
        if (newStatuses[tempId]) {
          newStatuses[attachment_id] = {
            ...newStatuses[tempId],
            status: status || "processing",
            uploadProgress: 100,
          };
          delete newStatuses[tempId];
        }
        return newStatuses;
      });

      setUploadingFiles((prev) => {
        const newSet = new Set(prev);
        newSet.delete(tempId);
        if (attachment_id) {
          newSet.add(attachment_id);
        }
        return newSet;
      });

      setUploadCancelTokens((prev) => {
        const newTokens = { ...prev };
        if (newTokens[tempId]) {
          newTokens[attachment_id] = newTokens[tempId];
          delete newTokens[tempId];
        }
        return newTokens;
      });

      file.attachmentId = attachment_id;

      return attachment_id;
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log(`Upload cancelled for ${file.name}`);
        setAttachmentStatuses((prev) => {
          const newStatuses = { ...prev };
          delete newStatuses[tempId];
          return newStatuses;
        });
        setProgressByAttachmentId((prev) => {
          const newProgress = { ...prev };
          delete newProgress[tempId];
          return newProgress;
        });
        return null;
      }

      console.error(`Error uploading ${file.name}:`, error);

      const failedId = attachment_id || tempId;
      setAttachmentStatuses((prev) => ({
        ...prev,
        [failedId]: {
          status: "failed",
          filename: file.name,
          error_message: error.response?.data?.error || error.message,
        },
      }));

      setProgressByAttachmentId((prev) => {
        const newProgress = { ...prev };
        delete newProgress[tempId];
        if (attachment_id && attachment_id !== tempId) {
          delete newProgress[attachment_id];
        }
        return newProgress;
      });

      throw error;
    } finally {
      setUploadingFiles((prev) => {
        const newSet = new Set(prev);
        newSet.delete(tempId);
        if (attachment_id) {
          newSet.delete(attachment_id);
        }
        if (newSet.size === 0) {
          setHasActiveUploads(false);
        }
        return newSet;
      });

      setUploadCancelTokens((prev) => {
        const newTokens = { ...prev };
        delete newTokens[tempId];
        if (attachment_id) {
          delete newTokens[attachment_id];
        }
        return newTokens;
      });
    }
  };

  const computeGlobalProgress = () => {
    const progressEntries = Object.values(progressByAttachmentId);
    const sessionTotal = uploadSession?.totalFiles;
    const totalFiles =
      sessionTotal && sessionTotal > 0
        ? sessionTotal
        : Math.max(progressEntries.length, 0);

    if (progressEntries.length === 0) {
      return {
        percent: 0,
        uploadedCount: 0,
        totalCount: totalFiles,
      };
    }

    const totalLoaded = progressEntries.reduce((sum, p) => sum + (p.loaded || 0), 0);
    const totalSize = progressEntries.reduce((sum, p) => sum + (p.total || 0), 0);

    const uploadedCount = progressEntries.filter(
      (p) => p.loaded === p.total && p.total > 0
    ).length;

    let percent = 0;
    if (totalSize > 0) {
      percent = Math.floor((totalLoaded * 100) / totalSize);
      if (uploadedCount < totalFiles) {
        percent = Math.min(percent, 99);
      } else {
        percent = 100;
      }
    }

    return { percent, uploadedCount, totalCount: totalFiles };
  };

  const retryAttachmentUpload = async (attachmentId, reportId) => {
    try {
      const attachmentInfo = attachmentStatuses[attachmentId];
      if (!attachmentInfo) {
        console.error(`Attachment info not found for ${attachmentId}`);
        return;
      }

      console.log(`Retry requested for attachment ${attachmentId}`);

      setAttachmentStatuses((prev) => ({
        ...prev,
        [attachmentId]: {
          ...prev[attachmentId],
          status: "pending",
          error_message: null,
        },
      }));

      setErrors((prev) => ({
        ...prev,
        general:
          "⚠️ Για επανάληψη απαιτείται να επιλέξετε ξανά το αρχείο. Προσθέστε το αρχείο και υποβάλετε ξανά.",
      }));
      setShowToast(true);
    } catch (error) {
      console.error(`Retry failed for attachment ${attachmentId}:`, error);
    }
  };

  const pollAttachmentStatus = async (reportId) => {
    try {
      const response = await axiosClient.get(
        `${backendUrl}/api/attachments?report_id=${reportId}`
      );

      const statusMap = {};
      response.data.attachments.forEach((att) => {
        const existingStatus = attachmentStatuses[att.id];
        statusMap[att.id] = {
          status: att.status,
          filename: att.filename,
          original_url: att.original_url,
          compressed_url: att.compressed_url,
          error_message: att.error_message,
          uploadProgress:
            att.status === "pending" && existingStatus?.uploadProgress !== undefined
              ? existingStatus.uploadProgress
              : att.status === "pending"
                ? 0
                : undefined,
        };
      });

      setAttachmentStatuses(statusMap);

      const allProcessingComplete =
        response.data.attachments.length > 0 &&
        response.data.attachments.every(
          (att) => att.status === "uploaded" || att.status === "error"
        );

      const hasErrors = response.data.attachments.some((att) => att.status === "error");

      const stillUploading = response.data.attachments.some((att) => {
        const existingStatus = attachmentStatuses[att.id];
        return (
          existingStatus?.uploadProgress !== undefined && existingStatus.uploadProgress < 100
        );
      });
      setHasActiveUploads(stillUploading);

      const hasBackgroundProcessing =
        response.data.attachments.length > 0 &&
        response.data.attachments.some(
          (att) => att.status === "pending_processing" || att.status === "processing"
        );
      setHasActiveProcessing(hasBackgroundProcessing && !stillUploading);

      if (allProcessingComplete) {
        setProgressByAttachmentId((prev) => {
          const updated = { ...prev };
          response.data.attachments.forEach((att) => {
            if (updated[att.id]) {
              updated[att.id] = {
                loaded: updated[att.id].total || 0,
                total: updated[att.id].total || 0,
              };
            }
          });
          return updated;
        });

        if (hasErrors && completedReportIdShown === reportId) {
          const errorAttachments = response.data.attachments.filter(
            (att) => att.status === "error"
          );
          const errorCount = errorAttachments.length;
          const totalCount = response.data.attachments.length;

          setErrors((prev) => ({
            ...prev,
            general: `⚠️ Σημείωση: ${errorCount} από ${totalCount} συνημμένα απέτυχαν κατά τη διαδικασία στο παρασκήνιο. Η αναφορά έχει ήδη υποβληθεί.`,
          }));
          setShowToast(true);
        }

        if (pollingInterval) {
          clearInterval(pollingInterval);
          setPollingInterval(null);
        }

        setTimeout(() => {
          setProgressByAttachmentId({});
          setHasActiveUploads(false);
          setHasActiveProcessing(false);
          setUploadSession(null);
        }, 2000);
      }

      if (allProcessingComplete && !stillUploading) {
        if (pollingInterval) {
          clearInterval(pollingInterval);
          setPollingInterval(null);
        }
      }

      return allProcessingComplete && !stillUploading;
    } catch (error) {
      console.error("Error polling attachment status:", error);
      return false;
    }
  };

  useEffect(() => {
    if (currentReportId && pollingInterval === null && !reportCompleted) {
      pollAttachmentStatus(currentReportId).catch((err) => {
        console.error("Background polling error (non-critical):", err);
      });

      const interval = setInterval(() => {
        if (currentReportId && !reportCompleted) {
          pollAttachmentStatus(currentReportId).catch((err) => {
            console.error("Background polling error (non-critical):", err);
          });
        } else {
          clearInterval(interval);
          setPollingInterval(null);
        }
      }, 5000);
      setPollingInterval(interval);

      return () => {
        if (interval) clearInterval(interval);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentReportId, reportCompleted]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (uploadingFiles.size > 0) {
        e.preventDefault();
        e.returnValue = "You have files uploading. Are you sure you want to leave?";
        return e.returnValue;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [uploadingFiles]);

  return {
    uploadProgress,
    setUploadProgress,
    uploadStats,
    setUploadStats,
    attachmentStatuses,
    setAttachmentStatuses,
    currentReportId,
    setCurrentReportId,
    pollingInterval,
    setPollingInterval,
    uploadingFiles,
    hasActiveUploads,
    setHasActiveUploads,
    hasActiveProcessing,
    setHasActiveProcessing,
    reportCompleted,
    setReportCompleted,
    progressByAttachmentId,
    setProgressByAttachmentId,
    completedReportIdShown,
    setCompletedReportIdShown,
    uploadCancelTokens,
    uploadSession,
    setUploadSession,
    resetEmployeeUploadState,
    resetForLogout,
    uploadFileAsync,
    uploadFilesBurstForSession: (files, reportId) =>
      uploadFilesBurstConcurrent(uploadFileAsync, files, reportId, UPLOAD_BURST_CONCURRENCY),
    computeGlobalProgress,
    retryAttachmentUpload,
    pollAttachmentStatus,
    stopPolling,
  };
}